import { logoutRequest } from '@cobliteam/store/src/Login';
import AppHeader from '@cobliteam/ui-business/components/AppHeader';
import AppJoyride from '@cobliteam/ui-business/components/AppJoyride';
import AppSidebar from '@cobliteam/ui-business/components/AppSidebar';
import { SidebarInset, SidebarProvider } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import { cn } from '@cobliteam/ui/utils';
import { type ReactNode, Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useRedirectAfterLogin from '../../hooks/useRedirectAfterLogin';
import { initializeThirdParty } from '../../libs/configureThirdParty';

// Preferences
import Preferences from '@cobliteam/preferences';

// Common Types
import { Location, locationName } from '@cobliteam/common-types';

// Hooks
import { useHubspot, useUser } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';

// Analytics
import { Analytics } from '@cobliteam/analytics';

// Cookie Manager
import { useOnboardingStore } from '@cobliteam/onboarding';
import LoadingPage from '../../pages/LoadingPage';

function NewAppLayout({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const hubspot = useHubspot();
  const history = useHistory();
  const currentLocation = locationName[history.location.pathname as Location];
  const { getSession, useGetUserData } = useUser();
  const { redirectSessionStorage } = useRedirectAfterLogin();
  const { progressBar } = useOnboardingStore();
  const { ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum } = useFeatureFlags();
  const session = getSession();
  const { data: userData } = useGetUserData();
  const currentFleet = session?.fleet;
  const fleets = userData?.userFleets;

  // Initialize core services
  useEffect(() => {
    Analytics.initialize();
    Preferences.initialize();
  }, []);

  // Handle redirect after login
  useEffect(() => {
    const redirectUrl = redirectSessionStorage();
    if (redirectUrl) {
      history.push(redirectUrl);
    }
  }, [history, redirectSessionStorage]);

  // Initialize third-party services
  useEffect(() => {
    const featureFlags = {
      ddRumSampleRate,
      ddRumReplaySampleRate,
      oneSignal,
      useDdRum,
    };
    initializeThirdParty({ session, userData, featureFlags });
  }, [session, userData, ddRumSampleRate, ddRumReplaySampleRate, oneSignal, useDdRum]);

  // Track page views
  useEffect(() => {
    if (!session) return;
    Analytics.pageview(currentLocation, { fleetId: session.fleet.id });
  }, [currentLocation, session]);

  useEffect(() => {
    hubspot.moveToRight();
  }, [history.location.pathname]);

  if (currentLocation === Location.ONBOARDING_SETUP) return null;

  return (
    <div className={cn(progressBar && 'pt-[54px]')}>
      <SidebarProvider>
        <AppSidebar fleets={fleets || []} currentFleet={currentFleet} />
        <SidebarInset>
          <AppHeader user={session.user} logout={() => dispatch(logoutRequest())} />
          <section id='data-table-virtualizer' className='relative h-screen-without-header w-full overflow-y-auto'>
            <Suspense fallback={<LoadingPage />}>{children}</Suspense>
          </section>
        </SidebarInset>
      </SidebarProvider>
      <AppJoyride />
      <div id='page-components' />
    </div>
  );
}

export default NewAppLayout;
